import { useState, useEffect, useCallback } from 'react';
import { pipelineService } from '@/lib/pipelineService';
import type { Deal, Stage, PipelineState } from '@/components/pipeline/types';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';

export function usePipelineState(pipelineId: string) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const [state, setState] = useState<PipelineState>({
    dealsById: {},
    stages: {},
    stageOrder: [],
    stageMeta: {},
    won: [],
    lost: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ─── LOAD ─────────────────────────────────
  const loadPipelineData = useCallback(async () => {
    if (!pipelineId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const [stagesData, dealsData] = await Promise.all([
        pipelineService.getStagesByPipeline(pipelineId),
        pipelineService.getDealsByPipeline(pipelineId),
      ]);

      // Build stageMeta + stageOrder
      const stageMeta: Record<string, Stage> = {};
      const stageOrder: string[] = [];
      const stages: Record<string, string[]> = {};

      const sorted = [...stagesData].sort((a, b) => a.position - b.position);
      sorted.forEach(s => {
        if (s.isNew) return; // Hide legacy "New" stages globally
        stageMeta[s.id] = s;
        stageOrder.push(s.id);
        stages[s.id] = [];
      });

      // Build dealsById + distribute into stages / won / lost
      const dealsById: Record<string, Deal> = {};
      const won: string[] = [];
      const lost: string[] = [];

      dealsData.forEach(deal => {
        dealsById[deal.id] = deal;

        if (deal.status === 'won') {
          won.push(deal.id);
          // Also put in won stage column if it exists
          if (deal.stage_id && stages[deal.stage_id] !== undefined) {
            stages[deal.stage_id].push(deal.id);
          }
        } else if (deal.status === 'lost') {
          lost.push(deal.id);
          if (deal.stage_id && stages[deal.stage_id] !== undefined) {
            stages[deal.stage_id].push(deal.id);
          }
        } else if (deal.stage_id && stages[deal.stage_id] !== undefined) {
          stages[deal.stage_id].push(deal.id);
        } else {
          // Fallback: If stage_id is missing or invalid, put it in the first active stage
          const firstStageId = stageOrder.find(id => !stageMeta[id]?.isWon && !stageMeta[id]?.isLost) || stageOrder[0];
          if (firstStageId && stages[firstStageId]) {
            stages[firstStageId].push(deal.id);
            deal.stage_id = firstStageId; // Update local reference so it works in UI
          }
        }
      });

      // Sort deals within each stage by position
      stageOrder.forEach(sid => {
        stages[sid].sort((a, b) => (dealsById[a]?.position ?? 0) - (dealsById[b]?.position ?? 0));
      });

      setState({ dealsById, stages, stageOrder, stageMeta, won, lost });
    } catch (err) {
      console.error('Error loading pipeline data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    loadPipelineData();
  }, [pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── DEAL OPERATIONS ──────────────────────

  const addDeal = useCallback(async (data: {
    title: string;
    description?: string;
    value?: number;
    priority?: string;
    source?: string;
    expectedCloseDate?: string;
    stageId?: string;
    contactId?: string;
    companyId?: string;
    assignedTo?: string;
    probability?: number;
    clientInfo?: Record<string, any>;
  }) => {
    if (!user || !currentWorkspace) return;
    await pipelineService.createDeal(pipelineId, currentWorkspace.id, user.id, data);
    await loadPipelineData();
  }, [pipelineId, user, currentWorkspace, loadPipelineData]);

  const updateDeal = useCallback(async (dealId: string, updates: Parameters<typeof pipelineService.updateDeal>[1]) => {
    try {
      // Optimistic update
      setState(prev => {
        const deal = prev.dealsById[dealId];
        if (!deal) return prev;

        let newStages = prev.stages;
        if (updates.stageId && updates.stageId !== deal.stage_id) {
          newStages = { ...prev.stages };
          // Remove from old stage
          if (deal.stage_id && newStages[deal.stage_id]) {
            newStages[deal.stage_id] = newStages[deal.stage_id].filter(id => id !== dealId);
          }
          // Add to new stage
          if (!newStages[updates.stageId]) {
            newStages[updates.stageId] = [];
          }
          newStages[updates.stageId] = [...newStages[updates.stageId], dealId];
        }

        return {
          ...prev,
          stages: newStages,
          dealsById: { ...prev.dealsById, [dealId]: { ...deal, ...mapUpdatesToState(updates) } },
        };
      });
      await pipelineService.updateDeal(dealId, updates);
    } catch (err) {
      console.error('Error updating deal:', err);
      await loadPipelineData();
      throw err;
    }
  }, [loadPipelineData]);

  const moveDeal = useCallback(async (dealId: string, toStageId: string, position: number) => {
    try {
      // Optimistic update
      setState(prev => {
        const deal = prev.dealsById[dealId];
        if (!deal) return prev;
        const newStages = { ...prev.stages };
        Object.keys(newStages).forEach(sid => {
          newStages[sid] = newStages[sid].filter(id => id !== dealId);
        });
        if (!newStages[toStageId]) newStages[toStageId] = [];
        newStages[toStageId].splice(position, 0, dealId);
        return {
          ...prev,
          stages: newStages,
          dealsById: { ...prev.dealsById, [dealId]: { ...deal, stage_id: toStageId, position } },
        };
      });

      await pipelineService.moveDeal(dealId, toStageId, position);
    } catch (err) {
      console.error('Error moving deal:', err);
      await loadPipelineData();
      throw err;
    }
  }, [loadPipelineData]);

  const markDealWon = useCallback(async (dealId: string, reason?: string) => {
    if (!user) return;
    await pipelineService.markDealWon(dealId, user.id, reason);
    await loadPipelineData();
  }, [user, loadPipelineData]);

  const markDealLost = useCallback(async (dealId: string, reason: string) => {
    if (!user) return;
    await pipelineService.markDealLost(dealId, user.id, reason);
    await loadPipelineData();
  }, [user, loadPipelineData]);

  const reopenDeal = useCallback(async (dealId: string, stageId: string) => {
    if (!user) return;
    await pipelineService.reopenDeal(dealId, user.id, stageId);
    await loadPipelineData();
  }, [user, loadPipelineData]);

  const deleteDeal = useCallback(async (dealId: string) => {
    setState(prev => {
      const { [dealId]: _omit, ...rest } = prev.dealsById;
      const newStages: PipelineState['stages'] = Object.fromEntries(
        Object.entries(prev.stages).map(([k, arr]) => [k, arr.filter(id => id !== dealId)])
      );
      return {
        ...prev,
        dealsById: rest,
        stages: newStages,
        won: prev.won.filter(id => id !== dealId),
        lost: prev.lost.filter(id => id !== dealId),
      };
    });
    await pipelineService.deleteDeal(dealId);
  }, []);

  // ─── STAGE OPERATIONS ────────────────────

  const addStage = useCallback(async (title: string, bgColor?: string) => {
    // Insert before Won/Lost (at second-to-last position)
    const nonFixed = state.stageOrder.filter(id => !state.stageMeta[id]?.isWon && !state.stageMeta[id]?.isLost);
    const position = nonFixed.length; // insert right before Won/Lost
    await pipelineService.createStage(pipelineId, title, position, 50, bgColor);
    await loadPipelineData();
  }, [pipelineId, state.stageOrder, state.stageMeta, loadPipelineData]);

  const updateStageTitle = useCallback(async (stageId: string, title: string) => {
    await pipelineService.updateStage(stageId, { title });
    setState(prev => ({
      ...prev,
      stageMeta: { ...prev.stageMeta, [stageId]: { ...prev.stageMeta[stageId], title } },
    }));
  }, []);

  const updateStageBgColor = useCallback(async (stageId: string, bgColor: string) => {
    await pipelineService.updateStage(stageId, { bgColor });
    setState(prev => ({
      ...prev,
      stageMeta: { ...prev.stageMeta, [stageId]: { ...prev.stageMeta[stageId], bgColor } },
    }));
  }, []);

  const deleteStage = useCallback(async (stageId: string) => {
    await pipelineService.deleteStage(stageId);
    await loadPipelineData();
  }, [loadPipelineData]);

  const reorderStages = useCallback(async (newStageOrder: string[]) => {
    setState(prev => ({ ...prev, stageOrder: newStageOrder }));
    await pipelineService.reorderStages(pipelineId, newStageOrder);
  }, [pipelineId]);

  // ─── ACTIVITIES ──────────────────────────

  const addActivity = useCallback(async (
    dealId: string,
    type: Parameters<typeof pipelineService.addActivity>[2],
    content: string,
    metadata?: Record<string, any>
  ) => {
    if (!user) return;
    await pipelineService.addActivity(dealId, user.id, type, content, metadata);
  }, [user]);

  return {
    state,
    loading,
    error,
    // Deal operations
    addDeal,
    updateDeal,
    moveDeal,
    markDealWon,
    markDealLost,
    reopenDeal,
    deleteDeal,
    // Stage operations
    addStage,
    updateStageTitle,
    updateStageBgColor,
    deleteStage,
    reorderStages,
    // Activities
    addActivity,
    // Reload
    reload: loadPipelineData,
  };
}

// Helper to translate camelCase updates to state field names
function mapUpdatesToState(updates: any): Partial<Deal> {
  const result: any = {};
  if (updates.title !== undefined) result.title = updates.title;
  if (updates.description !== undefined) result.description = updates.description;
  if (updates.value !== undefined) result.value = updates.value;
  if (updates.currency !== undefined) result.currency = updates.currency;
  if (updates.probability !== undefined) result.probability = updates.probability;
  if (updates.priority !== undefined) result.priority = updates.priority;
  if (updates.source !== undefined) result.source = updates.source;
  if (updates.expectedCloseDate !== undefined) result.expectedCloseDate = updates.expectedCloseDate;
  if (updates.stageId !== undefined) result.stage_id = updates.stageId;
  if (updates.contactId !== undefined) result.contactId = updates.contactId;
  if (updates.companyId !== undefined) result.companyId = updates.companyId;
  if (updates.assignedTo !== undefined) result.assignedTo = updates.assignedTo;
  if (updates.clientInfo !== undefined) result.clientInfo = updates.clientInfo;
  return result;
}
